import dompurify from 'dompurify';
import { h, FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as v4uuid } from 'uuid';
import {
  setDocument,
  setDocumentRef,
  setIsProcessing,
} from '~src/domains/common/state/commonReducer';
import {
  documentSelector,
  localeSelector,
} from '~src/domains/common/state/commonSelectors';
import * as styles from '~src/styles/domains/common/components/documents.module.scss';
import {
  formatDocumentLinks,
  translateDocument,
} from '~src/utils/documents/documents';
import { trackPageMount } from '~src/utils/tracking';
import { Page } from '../enums/api';
import SimpleButton from './Button';

const sanitize = dompurify.sanitize;

const DocumentView: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const docRef = useRef(null);
  const currDoc = useSelector(documentSelector);
  const currLocale = useSelector(localeSelector);

  useEffect(() => {
    dispatch(setIsProcessing(false));
    dispatch(trackPageMount(Page.DOCUMENT_VIEWER));
    formatDocumentLinks();
  }, []);

  useEffect(() => {
    if (currDoc?.id) {
      dispatch(translateDocument(currDoc.id, currLocale, i18n));
    }
  }, [currDoc.id, currLocale]);

  useEffect(() => {
    dispatch(setDocumentRef(docRef.current));
  }, [docRef]);

  const closeHandler = () => {
    dispatch(
      setDocument({
        ...currDoc,
        show: false,
      }),
    );
  };

  return (
    <div className={styles['document-view-wrapper']}>
      <div className={styles.wrapper}>
        <div
          ref={docRef}
          id={`document-${v4uuid()}`}
          className={`document ${styles.document} `}
          name='document'
          tabIndex={-1}
          role='document'
          aria-live='assertive'
          dangerouslySetInnerHTML={{
            __html: currDoc.content ? sanitize(currDoc.content) : '',
          }}
        ></div>
      </div>
      <div className={styles['close-footer']}>
        <SimpleButton
          classLabel={`btn-primary ${styles['button-close']}`}
          label={t('common:document-view.buttons.close.label')}
          onClickPost={closeHandler}
          shouldTrackClick={false}
        />
      </div>
    </div>
  );
};

export default DocumentView;
